// store/authThunks.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import * as authAPI from 'api/authApi';
import { UserRole } from './authSlice';

interface LoginPayload {
  email: string;
  password: string;
}

interface BackendUser {
  id: number;
  name: string;
  email: string;
  company_id: number | null;
  department_id: number | null;
  is_active: boolean;
  role_name: string | null;
  role_id: number;
  company_name: string;
  department_name: string;
}

const mapBackendRoleToFrontend = (backendRole: string | null): UserRole => {
  if (!backendRole) return 'super_user';

  const roleMap: Record<string, UserRole> = {
    superadmin: 'super_user',
    admin: 'company_admin',
    user: 'user',
    super_user: 'super_user',
    company_admin: 'company_admin'
  };

  const normalizedRole = backendRole.toLowerCase().trim();
  return roleMap[normalizedRole] || 'user';
};

const isValidFrontendRole = (role: string): role is UserRole => {
  return ['super_user', 'company_admin', 'user'].includes(role as UserRole);
};

const determineRoleFromUserData = (userData: BackendUser): UserRole => {
  if (userData.role_id === 1) return 'super_user';
  if (userData.role_id === 2) return 'company_admin';
  if (userData.role_id === 3) return 'user';

  if (
    userData.email?.includes('superadmin') ||
    userData.email?.includes('admin')
  ) {
    return 'super_user';
  }

  return 'user';
};

export const loginUser = createAsyncThunk<
  {
    user: {
      id: number;
      name: string;
      email: string;
      companyId: string | null;
      departmentId: string | null;
      is_active: boolean;
      role_name: string;
      company_name: string;
      department_name: string;
      role_id: number;
    };
    tokens: {
      access_token: string;
      refresh_token: string;
      token_type: string;
    };
  },
  LoginPayload,
  { rejectValue: string }
>('auth/loginUser', async (payload, { rejectWithValue }) => {
  try {
    const loginData = await authAPI.login(payload);
    const { access_token, refresh_token, token_type } = loginData;

    if (!access_token || !refresh_token) {
      return rejectWithValue('Invalid token response');
    }

    const userData: BackendUser = await authAPI.getUserProfile(access_token);

    if (!userData.is_active) {
      return rejectWithValue('Account is deactivated');
    }

    let frontendRole: UserRole;

    if (!userData.role_name) {
      frontendRole = determineRoleFromUserData(userData);
    } else {
      frontendRole = mapBackendRoleToFrontend(userData.role_name);
    }

    if (!isValidFrontendRole(frontendRole)) {
      return rejectWithValue('Invalid user role configuration');
    }

    // --------------------------
    // Store tokens
    sessionStorage.setItem('access_token', access_token);
    sessionStorage.setItem('refresh_token', refresh_token);

    // Store user info for RoleProtectedRoute
    // Store user info for session - USE THE SAME STRUCTURE AS AuthState
    const userForSession = {
      isLoggedIn: true,
      email: userData.email,
      name: userData.name,
      role: frontendRole,
      companyId: userData.company_id?.toString() || null,
      companies: [], // ← ADD THIS to match AuthState
      loginTime: Date.now(),
      expiresAt: Date.now() + 1000 * 60 * 60 * 2,
      access_token: access_token, // ← ADD THIS
      refresh_token: refresh_token, // ← ADD THIS
      token_type: token_type // ← ADD THIS
    };
    sessionStorage.setItem('user', JSON.stringify(userForSession));
    // --------------------------

    return {
      user: {
        id: userData.id,
        name: userData.name,
        email: userData.email,
        companyId: userData.company_id?.toString() || null,
        departmentId: userData.department_id?.toString() || null,
        is_active: userData.is_active,
        role_name: frontendRole,
        company_name: userData.company_name,
        department_name: userData.department_name,
        role_id: userData.role_id
      },
      tokens: {
        access_token,
        refresh_token,
        token_type
      }
    };
  } catch (error: unknown) {
    // --------------------------
    // Clean up sessionStorage on error
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user');
    // --------------------------

    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail?.[0]?.msg ||
        'Login failed';
      return rejectWithValue(errorMessage);
    }

    return rejectWithValue('Login failed');
  }
});
